/**
 * PDF Conversion Service
 * Handles PDF to tutorial conversion using Gemini AI
 */

import { TutorialStep } from '../types';
import { aiGlossaryService } from './aiGlossaryService';

export interface PDFConversionResult {
  success: boolean;
  tutorial?: {
    title: string;
    description: string;
    steps: TutorialStep[];
  };
  draftId?: string;
  message?: string;
  glossaryResult?: {
    success: boolean;
    keywords: Array<{
      term: string;
      definition: string;
      category: string;
      aliases?: string[];
    }>;
    message?: string;
    error?: string;
  };
  error?: string;
}

export interface PDFUploadProgress {
  stage: 'uploading' | 'extracting' | 'processing' | 'extracting-images' | 'analyzing-images' | 'generating' | 'complete';
  progress: number;
  message: string;
  details?: {
    currentImage?: number;
    totalImages?: number;
    currentBatch?: number;
    totalBatches?: number;
    extractedImages?: number;
    educationalImages?: number;
    filteredImages?: number;
    currentStage?: string;
  };
}

class PDFConversionService {
  private apiBaseUrl: string;

  constructor() {
    const raw = import.meta.env.VITE_VIAPHOTON_API_URL || '/api';
    // Normalize: ensure that hosts like http://localhost:3001 include /api
    let normalized = raw.replace(/\/$/, '');
    const needsApiSuffix = /localhost:3001$/.test(normalized) || /kb-api\./.test(normalized);
    if (needsApiSuffix && !/\/api$/.test(normalized)) {
      normalized += '/api';
    }
    this.apiBaseUrl = normalized;
  }

  /**
   * Convert PDF to tutorial format using background processing
   */
  async convertPDFToTutorial(
    pdfFile: File,
    onProgress?: (progress: PDFUploadProgress) => void,
    options?: {
      contentDepth?: 'simplified' | 'technical' | 'in-depth';
      processImages?: boolean;
    }
  ): Promise<PDFConversionResult> {
    let pdfId: string | undefined;
    
    try {
      // Stage 1: Upload PDF
      onProgress?.({
        stage: 'uploading',
        progress: 10,
        message: '📁 Uploading PDF file...'
      });

      const formData = new FormData();
      formData.append('pdf', pdfFile);

      const uploadResponse = await fetch(`${this.apiBaseUrl}/pdf/upload`, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      const uploadData = await uploadResponse.json();
      pdfId = uploadData.pdfId;

      // Stage 2: Extract text from PDF
      onProgress?.({
        stage: 'extracting',
        progress: 20,
        message: '📄 Extracting text from PDF...'
      });

      const extractResponse = await fetch(`${this.apiBaseUrl}/pdf/extract/${pdfId}`, {
        method: 'POST'
      });

      if (!extractResponse.ok) {
        throw new Error(`Text extraction failed: ${extractResponse.statusText}`);
      }

      const extractData = await extractResponse.json();
      const extractedText = extractData.text;

      // Stage 3: Start background processing and return draft ID
      onProgress?.({
        stage: 'processing',
        progress: 30,
        message: 'Starting background processing...'
      });

      const backgroundResponse = await fetch(`${this.apiBaseUrl}/pdf/process-background`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pdfId,
          text: extractedText,
          contentDepth: options?.contentDepth || 'technical',
          processImages: options?.processImages !== false // Default to true if not specified
        })
      });

      if (!backgroundResponse.ok) {
        throw new Error(`Background processing failed: ${backgroundResponse.statusText}`);
      }

      const backgroundData = await backgroundResponse.json();
      const draftId = backgroundData.draftId;

      onProgress?.({
        stage: 'complete',
        progress: 100,
        message: 'PDF processing started in background! Check drafts to monitor progress.'
      });

      return {
        success: true,
        draftId: draftId,
        message: 'PDF processing started in background. You can check the drafts section to monitor progress.'
      };

    } catch (error) {
      console.error('PDF conversion error:', error);
      
      // Clean up the uploaded PDF file even if processing failed
      try {
        if (typeof pdfId !== 'undefined') {
          await this.cleanupPdf(pdfId);
        }
      } catch (cleanupError) {
        console.warn('Failed to cleanup PDF file after error:', cleanupError);
      }
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Convert PDF URL to tutorial format using background processing
   */
  async convertPDFUrlToTutorial(
    pdfUrl: string,
    onProgress?: (progress: PDFUploadProgress) => void
  ): Promise<PDFConversionResult> {
    try {
      // Stage 1: Download PDF
      onProgress?.({
        stage: 'uploading',
        progress: 10,
        message: '📥 Downloading PDF from URL...'
      });

      const downloadResponse = await fetch(`${this.apiBaseUrl}/pdf/download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url: pdfUrl })
      });

      if (!downloadResponse.ok) {
        throw new Error(`PDF download failed: ${downloadResponse.statusText}`);
      }

      const downloadData = await downloadResponse.json();
      const pdfId = downloadData.pdfId;

      // Continue with the same process as file upload
      return await this.continueConversionFromPdfId(pdfId, onProgress);

    } catch (error) {
      console.error('PDF URL conversion error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Continue conversion process from PDF ID using background processing
   */
  private async continueConversionFromPdfId(
    pdfId: string,
    onProgress?: (progress: PDFUploadProgress) => void
  ): Promise<PDFConversionResult> {
    try {
      // Stage 2: Extract text from PDF
      onProgress?.({
        stage: 'extracting',
        progress: 30,
        message: 'Extracting text from PDF...'
      });

      const extractResponse = await fetch(`${this.apiBaseUrl}/pdf/extract/${pdfId}`, {
        method: 'POST'
      });

      if (!extractResponse.ok) {
        throw new Error(`Text extraction failed: ${extractResponse.statusText}`);
      }

      const extractData = await extractResponse.json();
      const extractedText = extractData.text;

      // Stage 3: Start background processing and return draft ID
      onProgress?.({
        stage: 'processing',
        progress: 50,
        message: 'Starting background processing...'
      });

      const backgroundResponse = await fetch(`${this.apiBaseUrl}/pdf/process-background`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pdfId,
          text: extractedText
        })
      });

      if (!backgroundResponse.ok) {
        throw new Error(`Background processing failed: ${backgroundResponse.statusText}`);
      }

      const backgroundData = await backgroundResponse.json();
      const draftId = backgroundData.draftId;

      onProgress?.({
        stage: 'complete',
        progress: 100,
        message: 'PDF processing started in background! Check drafts to monitor progress.'
      });

      return {
        success: true,
        draftId: draftId,
        message: 'PDF processing started in background. You can check the drafts section to monitor progress.'
      };

    } catch (error) {
      console.error('PDF conversion error:', error);
      
      // Clean up the uploaded PDF file even if processing failed
      try {
        await this.cleanupPdf(pdfId);
      } catch (cleanupError) {
        console.warn('Failed to cleanup PDF file after error:', cleanupError);
      }
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Clean up uploaded PDF file
   */
  private async cleanupPdf(pdfId: string): Promise<void> {
    try {
      await fetch(`${this.apiBaseUrl}/pdf/cleanup/${pdfId}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.warn('Failed to cleanup PDF:', error);
    }
  }
}

export const pdfConversionService = new PDFConversionService();
export default pdfConversionService;